import {Button, Dropdown, DropdownItem, Tooltip} from 'platform/components';
import {Center, Hide, ThemeIconKey} from 'platform/foundation';
import {match} from 'ts-pattern';

import {always} from 'ramda';

import i18n from '@dms/i18n';

import {useBoolean} from 'shared/web';

import {useSelectedAuthProfile} from '../hooks/useSelectedAuthProfile';
import {HeaderButtonWrapper} from './HeaderButtonWrapper';
import {TopBarActionContainer} from './TopBarActionContainer';

export function AuthProfileSelect() {
  const [isDropdownOpen, openDropdown, closeDropdown] = useBoolean();

  const {
    isLoading,
    isError,
    hasNoProfiles,
    profileOptions,
    selectedAuthProfile,
    setSelectedAuthProfile,
  } = useSelectedAuthProfile();

  const fullSelectedAuthProfile = selectedAuthProfile
    ? profileOptions.find(
        (profile) => profile.authorizationProfileId === selectedAuthProfile.authorizationProfileId
      )
    : null;
  const selectedProfileName = fullSelectedAuthProfile
    ? fullSelectedAuthProfile.name
    : i18n.t('entity.header.labels.withoutAuthorization');

  const handleProfileChange = (authProfileId: string, authorizationId: string) => {
    setSelectedAuthProfile({authorizationProfileId: authProfileId, authorizationId});
    closeDropdown();
  };

  const dropdownButtonIcon = match<[boolean, boolean], ThemeIconKey | undefined>([
    hasNoProfiles,
    isDropdownOpen,
  ])
    .with([false, true], always('navigation/arrow_drop_up'))
    .with([false, false], always('navigation/arrow_drop_down'))
    .otherwise(always(undefined));

  return (
    <Hide when={isError}>
      <Dropdown
        closeOnSelect
        isOpen={isDropdownOpen}
        isDisabled={isLoading || hasNoProfiles}
        onOpen={openDropdown}
        onClose={closeDropdown}
        dropdownControl={
          <TopBarActionContainer>
            <Center height={12}>
              <HeaderButtonWrapper $isOpen={isDropdownOpen}>
                <Tooltip
                  label={i18n.t('entity.header.labels.noAuthorizationProfile')}
                  isDisabled={!hasNoProfiles}
                >
                  <Button
                    aria-label="topBar"
                    variant="ghost"
                    isDisabled={isLoading || hasNoProfiles}
                    rightIcon={dropdownButtonIcon}
                    title={`${i18n.t('page.settings.labels.profile')}: ${selectedProfileName}`}
                  />
                </Tooltip>
              </HeaderButtonWrapper>
            </Center>
          </TopBarActionContainer>
        }
        placement="bottom-start"
        isLazy
        lazyBehavior="unmount"
      >
        {profileOptions.map((profile) => (
          <DropdownItem
            key={profile.authorizationProfileId}
            label={profile.name}
            onClick={() =>
              handleProfileChange(profile.authorizationProfileId, profile.authorizationId)
            }
          />
        ))}

        <DropdownItem
          key="withoutAuthorization"
          label={i18n.t('entity.header.labels.withoutAuthorization')}
          onClick={() => setSelectedAuthProfile(null)}
        />
      </Dropdown>
    </Hide>
  );
}
